import React, { useState } from 'react';
import { X } from '@lognai/synapse-design-system-v2/icons';
import {
  Badge,
  Button,
  useToast,
} from '@lognai/synapse-design-system-v2';

import { DismissConfirmDialog } from './DismissConfirmDialog';
import { FeedbackButtons } from '../FeedbackButtons';
import { entityRouteFor } from '../../utils/entity-route-for';
import { SEVERITY_TONE, SEVERITY_LABEL } from '../../utils/severity';
import { useDismissInsight } from '../../hooks/use-insights';
import type { AiInsight } from '../../api/insights';

export interface InsightRowProps {
  insight: AiInsight;
  onNavigate?: (url: string) => void;
}

const css = `
  @property --ai-border-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }
  @keyframes ai-border-spin {
    to { --ai-border-angle: 360deg; }
  }
  .ai-insight-row {
    position: relative;
    background: color-mix(in srgb, var(--primary) 5%, transparent);
    border: none;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.05);
    transition: box-shadow 0.25s ease;
  }
  [data-theme="dark"] .ai-insight-row {
    background: linear-gradient(to bottom, rgba(18,22,34,0) 0%, rgba(18,22,34,1) 100%), color-mix(in srgb, var(--primary) 5%, transparent);
    box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2);
  }
  [data-theme="gray"] .ai-insight-row {
    background: linear-gradient(to bottom, rgba(24,24,27,0) 0%, rgba(24,24,27,1) 100%), color-mix(in srgb, var(--primary) 5%, transparent);
    box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2);
  }
  .ai-insight-row::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    padding: 1px;
    background:
      conic-gradient(
        from var(--ai-border-angle),
        transparent 0deg,
        transparent 250deg,
        var(--primary) 300deg,
        #fff 340deg,
        transparent 360deg
      ),
      color-mix(in srgb, var(--primary) 25%, transparent);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    animation: ai-border-spin 4s linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .ai-insight-row::before { animation: none; }
  }
  .ai-insight-row:hover {
    box-shadow: -2px 6px 12px color-mix(in srgb, var(--primary) 10%, transparent), 2px 6px 12px color-mix(in srgb, var(--primary) 10%, transparent);
  }
  .ai-insight-row-clickable { cursor: pointer; }
`;

export const InsightRow: React.FC<InsightRowProps> = ({ insight, onNavigate }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();
  const dismiss = useDismissInsight();

  const targetUrl = entityRouteFor(insight.scope);
  const clickable = Boolean(targetUrl);

  const handleRowClick = () => {
    if (targetUrl && onNavigate) onNavigate(targetUrl);
  };

  const handleDismissConfirm = async (reason?: string) => {
    try {
      await dismiss.mutateAsync({ id: insight.id, reason });
      setDialogOpen(false);
      toast({ title: 'Insight dismissed', description: 'Hidden for your company for 30 days.' });
    } catch (err) {
      toast({ title: 'Dismiss failed', description: (err as Error).message, tone: 'destructive' });
    }
  };

  const rowClickHandlers = clickable
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: handleRowClick,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') handleRowClick();
        },
      }
    : {};

  return (
    <>
      <style>{css}</style>
      <div
        {...rowClickHandlers}
        aria-disabled={!clickable}
        className={`ai-insight-row${clickable ? ' ai-insight-row-clickable' : ''}`}
        data-testid={`insight-row-${insight.id}`}
      >
        {/* Header row — mirrors InlineAiCard header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.25, color: 'var(--foreground)' }}>
              {insight.title}
            </div>
            <Badge tone={SEVERITY_TONE[insight.severity]} variant="subtle" size="sm">
              {SEVERITY_LABEL[insight.severity]}
            </Badge>
          </div>

          {/* Right side: feedback + dismiss */}
          <span
            style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <FeedbackButtons
              targetKind="insight"
              targetRef={{ insightId: insight.id, ruleId: insight.ruleId }}
              snapshot={{}}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setDialogOpen(true);
              }}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </Button>
          </span>
        </div>

        {/* Summary */}
        <div style={{ fontSize: 14, fontWeight: 400, lineHeight: '20px', color: 'var(--foreground)', marginBottom: 8 }}>
          {insight.summary}
        </div>

        {/* Evidence badges — analogous to InlineAiCard signals */}
        {insight.evidence.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {insight.evidence.slice(0, 4).map((e, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,0.7)', border: '1px solid color-mix(in srgb, var(--primary) 20%, transparent)', backdropFilter: 'blur(2px)', fontSize: 12 }}>
                <span style={{ color: 'var(--muted-foreground)', fontWeight: 400 }}>{e.label.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (c) => c.toUpperCase())}</span>
                <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>{String(e.value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</span>
              </span>
            ))}
          </div>
        )}

        <DismissConfirmDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onConfirm={handleDismissConfirm}
          submitting={dismiss.isPending}
        />
      </div>
    </>
  );
};
